import { ReactElement, useContext, useState } from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@appsumo/dorado-react';

import { useEvent } from '~/lib/events';
import { currencyWithCents } from '~/lib/format';
import { CartContext } from '~/contexts/cart';
import { GlobalContext } from '~/contexts/global';
import { COUNTRIES_REQUIRE_POSTAL_CODE } from '~/constants/checkout';

import { CheckoutButton, PlusUpsell, SavedForLater } from '~/components/cart';
import Discounts from '~/components/checkout/Summary/Discounts';
import Guarantee from '~/components/checkout/Global/Guarantee';
import Credits from '~/components/checkout/Global/Credits';
import { CampaignIncentive } from '~/components/checkout/Summary/CampaignIncentive';
import { KlarnaMessage } from '~/components/global/KlarnaMessage';

export default function Summary({
  hasPlusInCart = false,
}: {
  hasPlusInCart?: boolean;
}): ReactElement {
  const {
    campaigns: { showCampaignIncentive },
  } = useContext(GlobalContext);
  const { billingDetails, cart, user } = useContext(CartContext);
  const router = useRouter(); // Used useRouter instead of link, cleaner to disable click

  const [couponError, setCouponError] = useState('');
  const [isCouponLoading, setIsCouponLoading] = useState(false);

  // Show loading states when api requests are in progress
  // Kept these as separate states to prevent race conditions
  // Didn't use 1 loading event for all api calls to allow granular control in other components
  useEvent('coupon:loading', () => setIsCouponLoading(true));
  useEvent('coupon:reset', () => setIsCouponLoading(false));

  const postalCodeText =
    COUNTRIES_REQUIRE_POSTAL_CODE.includes(
      `${billingDetails?.country?.value}`,
    ) && !!billingDetails.postalCode
      ? `, ${billingDetails.postalCode}`
      : '';

  return (
    <div className="lg:col-start-2 lg:col-end-2 lg:row-start-1">
      <div className="flex flex-col gap-4 lg:mt-0">
        {showCampaignIncentive && <CampaignIncentive className="lg:hidden" />}
        <div className="flex flex-col gap-4 rounded bg-iceberg p-4">
          <div className="flex">
            <div className="grow">Subtotal</div>
            <div className="font-medium" data-testid="summary-cart-subtotal">
              {currencyWithCents(cart?.subtotal)}
            </div>
          </div>
          {/* Do not show discounts if Freebie */}
          {!!cart?.subtotal && (
            <Discounts
              couponError={couponError}
              isCouponLoading={isCouponLoading}
              setCouponError={setCouponError}
              isDisabled={false}
              cart={cart}
            />
          )}
          <div>
            <div className="flex">
              <div className="grow">Sales tax</div>
              <div
                className={clsx(
                  cart?.tax ? 'font-medium' : 'font-light italic text-grace',
                )}
                data-testid="summary-cart-tax"
              >
                {cart?.tax ? currencyWithCents(cart.tax) : 'Pending'}
              </div>
            </div>

            {billingDetails?.country?.label && (
              <div className="mt-2 text-grace">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="mx-2" />
                {billingDetails.country.label}
                {postalCodeText}
              </div>
            )}
          </div>
          <div className="border-t border-sundog" />
          <Credits cart={cart} />
          <div className="flex font-bold">
            <div className="grow">
              Total{' '}
              <span className="hidden font-normal text-grace lg:inline-block">
                before tax
              </span>
            </div>
            <div data-testid="summary-cart-total">
              {currencyWithCents(cart?.total)}
            </div>
          </div>
        </div>
        {!hasPlusInCart && (
          <KlarnaMessage
            className="lg:hidden"
            currentBillingCountry={`${billingDetails?.country?.value || ''}`}
          />
        )}
        <CheckoutButton />
        <Button
          tertiary
          className="hidden w-full text-center lg:block"
          onClick={() => router.push('/browse')}
          data-testid="summary-cart-continue-shopping"
        >
          Continue shopping
        </Button>
        <Guarantee />
        <PlusUpsell
          cart={cart}
          user={user}
          hasPlusInCart={hasPlusInCart}
          className="lg:hidden"
        />
        <SavedForLater className="lg:hidden" />
      </div>
    </div>
  );
}

export { Summary };
